import { CheckCircle, CornerDownRight, Edit, Eye, Tag, XCircle } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useTranslation } from 'react-i18next';

import { GetRegistrationSummaryQuery as GetRegistrationSummary, Permission } from '__generated__/graphql';

import { ContainerWithOptions, LinkOption, Option } from '../../../../common/Common/OptionsDropdown';
import { useSession } from '../../../Auth/SessionProvider';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import ParticipantFieldValue from '../../../../common/ParticipantFields/ParticipantFieldValue';
import SellableLabel from '../../../../common/Common/SellableLabel';
import UI from '../../../../common/UI';
import useEditPurchaseModal from '../Orders/useEditPurchaseModal';
import useProject from '../../../useProject';
import useTogglePurchaseMutations from './useTogglePurchaseMutations';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];
type Upgrade = Registration['upgrades'][0];

export interface UpgradeDetailsProps {
  upgrade: Upgrade;
  registration: Registration;
  refetchQueries?: DocumentNode[];
}

const UpgradeDetails = ({
  upgrade, registration, refetchQueries = [GetRegistrationSummaryQuery],
}: UpgradeDetailsProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const project = useProject();

  const { activatePurchase, deactivatePurchase } = useTogglePurchaseMutations({
    refetchQueries,
  });

  const [openEditPurchaseModal] = useEditPurchaseModal({
    purchaseId: upgrade.purchase.id,
    eventId: registration.event.id,
    refetchQueries,
  });

  const muted = !upgrade.purchase.active;
  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  return (
    <UI.GridContainer sc={{ columns: '14px 1fr', gutter: 0.5 }}>
      <UI.Div>
        <UI.Icon sc={{ muted: true }}>
          <CornerDownRight />
        </UI.Icon>
      </UI.Div>
      <UI.GridContainer sc={{ gutter: 0.5 }}>
        <ContainerWithOptions
          renderOptions={() => (
            <>
              {hasEditPermission && (
                <Option
                  icon={<Edit />}
                  label={t('edit_purchase')}
                  onClick={() => openEditPurchaseModal()}
                />
              )}
              {hasEditPermission && upgrade.purchase.active && (
                <Option
                  icon={<XCircle />}
                  label={t('cancel_purchase')}
                  onClick={() => deactivatePurchase(upgrade.purchase)}
                />
              )}
              {hasEditPermission && !upgrade.purchase.active && registration.participant.active && (
                <Option
                  icon={<CheckCircle />}
                  label={t('reactivate_purchase')}
                  onClick={() => activatePurchase(upgrade.purchase)}
                />
              )}
              <LinkOption
                icon={<Eye />}
                label={t('view_order')}
                to={`/${project.id}/participants/${upgrade.purchase.order?.participant?.id}/orders/${upgrade.purchase.order?.id}`}
              />
            </>
          )}
        >
          <UI.Div sc={{ muted }}>
            <UI.Span sc={{ color: 'gray.800', fontWeight: 500, deleted: !upgrade.purchase.active, mr: 2 }}>
              {upgrade.product.title}
            </UI.Span>
            {upgrade.product_variant && (
              <SellableLabel type="product">
                {upgrade.product_variant.title}
              </SellableLabel>
            )}
          </UI.Div>
          {upgrade.purchase.promotion.title && (
            <UI.Span sc={{ muted: true }}>
              <UI.Icon>
                <Tag />
              </UI.Icon>
              {' '}
              {upgrade.purchase.promotion.title}
            </UI.Span>
          )}
        </ContainerWithOptions>

        {upgrade.participant_fields.length > 0 && (
          <UI.Blockquote>
            <UI.GridContainer sc={{ gutter: 0.5 }}>
              {upgrade.participant_fields.map((field) => (
                <ParticipantFieldValue
                  fieldEntry={{
                    value: null,
                    choice_entries: [],
                    field: {
                      id: field.id,
                    },
                    ...upgrade.participant_field_entries.filter(
                      (fieldEntry) => fieldEntry.field.id === field.id,
                    )[0],
                  }}
                  field={field}
                  key={field.id}
                />
              ))}
            </UI.GridContainer>
          </UI.Blockquote>
        )}
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export default UpgradeDetails;
